import { useEffect, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  trackEvent,
  trackUserInteraction,
} from "../../../../services/analytics-adapter";
import { convertPxToRem } from "../../../../utils";
import { MemberProfile } from "../../../../services/core-api-adapter";
import ButtonWithLoadingState from "@/features/Common/components/ButtonWithLoadingState";
import IconLoader from "@/features/Common/components/IconLoader";

interface PayGMedicalAssistanceCTAProps {
  memberProfile: MemberProfile | null;
  isLoading: boolean;
  onlyDisplayWhenActiveSession?: boolean;
}

export default function PayGMedicalAssistanceCTA({
  memberProfile,
  isLoading,
  onlyDisplayWhenActiveSession = false,
}: PayGMedicalAssistanceCTAProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasActiveSessions, setHasActiveSessions] = useState<boolean | null>(
    null
  );

  function onGetMedicalAssistanceButtonClick() {
    trackEvent({
      event: "action.getMedicalAssistanceInitiated",
      "getMedicalAssistance.source": "Home screen",
    });

    trackUserInteraction({
      linkText: "Home screen | getMedicalAssistanceInitiated",
      linkIntent: "navigational",
      linkScope: "button",
    });

    navigate("/home/get-medical-assistance/intercare");
  }

  useEffect(() => {
    if (memberProfile?.consultation.consultationResponses) {
      setHasActiveSessions(
        memberProfile.consultation.consultationResponses.length > 0
      );
    }
  }, [memberProfile, isLoading]);

  return (
    <>
      {isLoading === true && onlyDisplayWhenActiveSession === false && (
        <ButtonWithLoadingState sx={{ height: `${convertPxToRem(78)}` }} />
      )}

      {isLoading === false && (
        <>
          {hasActiveSessions === true && (
            <Button
              onClick={onGetMedicalAssistanceButtonClick}
              aria-label={t("GetMedicalAssistanceCTAButton.continue.label")}
              color="primary"
              size="large"
              fullWidth
              endIcon={<IconLoader icon="ArrowInCircleIcon" />}
              style={{ justifyContent: "space-between" }}
            >
              <Stack spacing={1} textAlign="left">
                <Typography variant="h4" color="primary.contrastText">
                  {t("GetMedicalAssistanceCTAButton.continue.label")}
                </Typography>
                {hasActiveSessions && (
                  <Typography
                    component="p"
                    variant="h5"
                    color="primary.contrastText"
                  >
                    {t("GetMedicalAssistanceCTAButton.continue.subtitle")}
                  </Typography>
                )}
              </Stack>
            </Button>
          )}

          {hasActiveSessions === false &&
            onlyDisplayWhenActiveSession === false && (
              <>
                <Button
                  onClick={onGetMedicalAssistanceButtonClick}
                  aria-label={t(
                    "GetMedicalAssistanceCTAButton.DirectToDoctor.label"
                  )}
                  color="primary"
                  size="large"
                  fullWidth
                  endIcon={<IconLoader icon="ArrowInCircleIcon" />}
                  style={{ justifyContent: "space-between" }}
                >
                  <Stack spacing={1} textAlign="left">
                    <Typography
                      component="p"
                      variant="h4"
                      color="primary.contrastText"
                    >
                      {t("GetMedicalAssistanceCTAButton.DirectToDoctor.label")}
                    </Typography>
                    <Typography
                      component="p"
                      variant="body2"
                      fontWeight="bold"
                      color="primary.contrastText"
                      style={{ marginTop: 0 }}
                    >
                      <Trans
                        i18nKey="GetMedicalAssistanceCTAButton.DirectToDoctor.priceText"
                        values={{
                          amount1:
                            memberProfile?.consultation.consultationRates[0]
                              ?.amountFormatted,
                          amount2:
                            memberProfile?.consultation.consultationRates[1]
                              ?.amountFormatted,
                        }}
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight="bold"
                          color="primary.contrastText"
                        ></Typography>
                      </Trans>
                    </Typography>
                  </Stack>
                </Button>
              </>
            )}
        </>
      )}
    </>
  );
}
